import { useState, useEffect, Fragment } from 'react';
import { api } from '../lib/api.js';
import { kioskApi } from '../lib/kiosk-api.js';

// Morning PAR on the daily sheet (CJ 2026-07-21). A collapsed drop-down the
// opener taps to see Tally's forecast prep sheet for this store — drinks by the
// gallon (iced latte split by milk), egg sandwiches & baked goods by the hour.

const card = { background: '#fff', border: '1px solid #eee', borderRadius: 10, overflow: 'hidden' };
const head = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 14px', cursor: 'pointer', width: '100%', background: 'none', border: 'none', textAlign: 'left', font: 'inherit' };
const label = { fontSize: 14, fontWeight: 700, color: '#212121' };
const sub = { fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 };
const num = { fontVariantNumeric: 'tabular-nums' };

function DrinkRows({ drinks }) {
  return (
    <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
      <tbody>
        {drinks.map((d) => (
          <Fragment key={d.label}>
            <tr style={{ borderTop: '1px solid #f0f0f0' }}>
              <td style={{ padding: '6px 0', fontWeight: 600 }}>{d.label}</td>
              <td style={{ padding: '6px 0', textAlign: 'right', ...num }}>
                <span style={{ fontSize: 18, fontWeight: 700 }}>{d.total}</span>
                <span style={{ fontSize: 11, color: '#757575', marginLeft: 3 }}>{d.unit === 'gal' ? 'gal' : 'ea'}</span>
              </td>
            </tr>
            {(d.breakdown || []).map((b) => (
              <tr key={d.label + b.label}>
                <td style={{ padding: '1px 0 1px 14px', fontSize: 12, color: '#757575' }}>{b.label}</td>
                <td style={{ padding: '1px 0', textAlign: 'right', fontSize: 12, color: '#757575', ...num }}>{b.value} gal</td>
              </tr>
            ))}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

function HourGrid({ title, matrix }) {
  if (!matrix || !matrix.rows || matrix.rows.length === 0) return null;
  const hr = (h) => `${h > 12 ? h - 12 : h}a`;
  return (
    <div style={{ marginTop: 12, overflowX: 'auto' }}>
      <div style={{ ...sub, marginBottom: 6 }}>{title}</div>
      <table style={{ width: '100%', minWidth: 380, fontSize: 13, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ ...sub, fontSize: 10 }}>
            <th style={{ textAlign: 'left', padding: '2px 0' }}>Item</th>
            {matrix.hours.map((h) => (
              <th key={h} style={{ textAlign: 'right', padding: '2px 4px', ...num }}>{hr(h)}</th>
            ))}
            <th style={{ textAlign: 'right', padding: '2px 0 2px 8px' }}>Day</th>
          </tr>
        </thead>
        <tbody>
          {matrix.rows.map((r) => (
            <tr key={r.label} style={{ borderTop: '1px solid #f0f0f0' }}>
              <td style={{ padding: '5px 0', fontWeight: 500 }}>{r.label}</td>
              {r.by_hour.map((v, i) => (
                <td key={i} style={{ padding: '5px 4px', textAlign: 'right', color: v === 0 ? '#bdbdbd' : '#212121', ...num }}>{v || '·'}</td>
              ))}
              <td style={{ padding: '5px 0 5px 8px', textAlign: 'right', fontWeight: 700, ...num }}>{r.day}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// On the store kiosk, pass kioskToken: the panel then asks the kiosk route for
// that store (location.key) with the Kiosk-Token instead of a signed-in user.
export function MorningParPanel({ location, kioskToken }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [state, setState] = useState('idle'); // idle | loading | error

  async function load() {
    if (!location?.name) return;
    setState('loading');
    try {
      const r = kioskToken
        ? await kioskApi('GET', `/api/kiosk/${location.key}/morning-par`, null, kioskToken)
        : await api('GET', `/api/operations/morning-par?location=${encodeURIComponent(location.name)}`);
      setData(r);
      setState('ready');
    } catch {
      setState('error');
    }
  }

  function toggle() {
    const next = !open;
    setOpen(next);
    if (next && !data && state !== 'loading') load();
  }

  // Tie the PAR to the selected store: when the location changes, drop the stale
  // data and refetch if the panel is open (otherwise it fetches fresh on expand).
  useEffect(() => {
    setData(null);
    setState('idle');
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.name]);

  return (
    <div style={card}>
      <button style={head} onClick={toggle} aria-expanded={open}>
        <span style={label}>☀ Morning PAR</span>
        <span style={{ ...sub, fontSize: 12 }}>{open ? 'hide ▾' : 'show ▸'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px' }}>
          {state === 'loading' && <div style={{ fontSize: 13, color: '#9e9e9e', padding: '4px 0' }}>Loading…</div>}
          {state === 'error' && <div style={{ fontSize: 13, color: '#c62828', padding: '4px 0' }}>Couldn’t load the PAR right now.</div>}
          {state === 'ready' && data && data.available === false && (
            <div style={{ fontSize: 13, color: '#9e9e9e', padding: '4px 0' }}>Morning PAR isn’t available for this store yet.</div>
          )}
          {state === 'ready' && data && data.available !== false && (
            <>
              <div style={{ fontSize: 12, color: '#757575', margin: '2px 0 6px' }}>
                Have ready by open{data.stale ? ' · (cached)' : ''}. {data.basis}
              </div>
              {data.event && (
                <div style={{ fontSize: 12, background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6, padding: '6px 8px', margin: '0 0 8px' }}>
                  <b>{data.event.note || 'Event'}</b> — forecast scaled ×{data.event.multiplier}.
                </div>
              )}
              <div style={{ ...sub, marginBottom: 4 }}>Drinks — have ready</div>
              <DrinkRows drinks={data.drinks || []} />
              <HourGrid title="Egg sandwiches — by the hour" matrix={data.sandwiches} />
              <HourGrid title="Baked goods — by the hour" matrix={data.baked} />
            </>
          )}
        </div>
      )}
    </div>
  );
}
